import React, { useMemo } from "react";
import "./index.scss";
import LendingPoolsRow from "./LendingPoolsRow";
import PairAddressContext from "../../contexts/PairAddress";
import QuestionHelper from "../QuestionHelper";
import { Card, Col, Container, Row, Spinner } from "react-bootstrap";
import {
  useGetWhitelistedOld,
  useGetWhitelistedPairs,
} from "../../hooks/useNetwork";
import { useState } from "react";
import CheckBox from "../CheckBox";
import FactoryContext from "../../contexts/Factory";
import useGenericCall from "../../hooks/useGenericCall";
import useOffchainInstance from "../../hooks/useOffchain";

export enum RateOption {
  weekly,
  daily,
  current,
}

/**
 * Generate a searchable lending pools table.
 */
export function LendingPoolsTable() {
  const getWhitelistedPairs = useGetWhitelistedPairs();
  const getWhitelistedOld = useGetWhitelistedOld();

  const [rateOption, setRateOption] = useState<RateOption>(RateOption.current);
  const rateOptionStr =
    rateOption === RateOption.weekly
      ? "7d "
      : rateOption === RateOption.daily
      ? "24h "
      : "";
  const [showOldPairs, setShowOldPairs] = useState<boolean>(false);

  const pairList = useGenericCall(
    useOffchainInstance(),
    async (offchainInstance) => {
      const filteredPairList = (await offchainInstance.getPairList()).filter(
        (pair) =>
          //getWhitelistedPairs(pair.factory).length === 0 ||
          getWhitelistedPairs(pair.factory).includes(pair.pairAddress) ||
          (showOldPairs &&
            getWhitelistedOld(pair.factory).includes(pair.pairAddress))
      );
      return await offchainInstance.getPairListWithData(filteredPairList);
    },
    [showOldPairs]
  );

  const orderedPairList = useMemo(() => {
    return pairList
      ? pairList.sort((a, b) => {
          if (a.totalBorrowsUSD > b.totalBorrowsUSD) return -1;
          return 1;
        })
      : null;
  }, [pairList]);

  if (!pairList) {
    return (
      <div className="spinner-container">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <Container className="mt-4">
        <Row>
          <Col sm={6} className="mt-2">
            <CheckBox
              value={showOldPairs}
              label="Show deprecated pairs"
              onChange={() => setShowOldPairs(!showOldPairs)}
            />
          </Col>
          <Col sm={6} className="text-right">
            Rates{" "}
            <QuestionHelper
              placement="left"
              text={
                "Time used for interest rates and Borrow APR assumed for Leveraged LP APR"
              }
            />
            <div className="selector ml-2">
              <div
                className={
                  "option" +
                  (rateOption === RateOption.weekly ? " selected" : "")
                }
                onClick={() => setRateOption(RateOption.weekly)}
              >
                <span>7 days average</span>
              </div>
              <div
                className={
                  "option" +
                  (rateOption === RateOption.daily ? " selected" : "")
                }
                onClick={() => setRateOption(RateOption.daily)}
              >
                <span>24 hours average</span>
              </div>
              <div
                className={
                  "option" +
                  (rateOption === RateOption.current ? " selected" : "")
                }
                onClick={() => setRateOption(RateOption.current)}
              >
                <span>Current</span>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <Container className="mt-2">
        <Row>
          <Col sm={12}>
            <Card className="overflow-hidden">
              <Card.Body>
                <div className="pairs-table">
                  <div className="pairs-table-header row">
                    <div className="col-7 col-md-5 col-lg-4">Market</div>
                    <div className="col d-none d-md-block">Total Supply</div>
                    <div className="col d-none d-md-block">Total Borrowed</div>
                    <div className="col d-none d-lg-block">
                      {rateOptionStr}Supply APR
                    </div>
                    <div className="col d-none d-lg-block">
                      {rateOptionStr}Borrow APR
                    </div>
                    <div className="col-5 col-md-3 col-lg-2 text-center">
                      Leveraged LP APR{" "}
                      <QuestionHelper
                        placement="left"
                        text={
                          "Based on last 7 days trading fees using the specified leverage"
                        }
                      />
                    </div>
                  </div>
                  {orderedPairList.map((data) => (
                    <FactoryContext.Provider
                      value={data.factory}
                      key={data.factory + data.pairAddress}
                    >
                      <PairAddressContext.Provider value={data.pairAddress}>
                        <LendingPoolsRow rateOption={rateOption} />
                      </PairAddressContext.Provider>
                    </FactoryContext.Provider>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
